/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { createRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Swiper, {
    Autoplay, FreeMode, Navigation, Pagination, Scrollbar
} from 'swiper';

import {
    mapDispatchToProps,
    mapStateToProps,
    ProductListContainer as SourceProductListContainer
} from 'SourceComponent/ProductList/ProductList.container';
import { CategoryContentPushType } from 'Type/Category.type';
import { setQueryParams } from 'Util/Url';

import './ProductList.override.style';

/** @namespace PeggysagePwa/Component/ProductList/Container */
export class ProductListContainer extends SourceProductListContainer {
    static propTypes = {
        ...SourceProductListContainer.propTypes,
        contentPushes: PropTypes.arrayOf(CategoryContentPushType),
        categoryId: PropTypes.number
    };

    static defaultProps = {
        ...SourceProductListContainer.defaultProps,
        // pageSize: 30, // CHANGE NUMBER OF PRODUCTS PER PAGE HERE
        contentPushes: [],
        categoryId: null
    };

    containerFunctions = {
        ...this.containerFunctions,
        onShowMoreButtonClick: this.onShowMoreButtonClick.bind(this)
    };

    productListRef = createRef();

    componentDidMount() {
        super.componentDidMount();

        this.handleProductSlider();
    }

    /**
     * Override: disable page request when page url parameter change with infinite scroll enabled
     * + handle slider product on widgets
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const {
            sort,
            search,
            filter,
            pages
        } = this.props;

        const {
            sort: prevSort,
            search: prevSearch,
            filter: prevFilter,
            location: prevLocation
        } = prevProps;

        const { pagesCount } = this.state;
        const pagesLength = Object.keys(pages).length;

        if (pagesCount !== pagesLength) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ pagesCount: pagesLength });
        }

        const prevPage = this._getPageFromUrl(prevLocation);
        const currentPage = this._getPageFromUrl();

        if (search !== prevSearch
            || (currentPage !== prevPage && !this._getIsInfiniteLoaderEnabled()) // CHANGED HERE
            || JSON.stringify(sort) !== JSON.stringify(prevSort)
            || JSON.stringify(filter) !== JSON.stringify(prevFilter)
        ) {
            this.requestPage(this._getPageFromUrl());
        }

        this.handleProductSlider();
    }

    handleProductSlider() {
        const { isWidget } = this.props;

        if (isWidget) {
            const { current: productListElem } = this.productListRef;

            if (productListElem) {
                if (productListElem.closest('[data-content-type="products"][data-appearance="carousel"]')) {
                    // CMS Slider
                    this._createCmsSlider(productListElem);
                } else if (productListElem.closest('[data-content-type="Tutorial_Step"]')) {
                    // TUTO
                    this._createTutorialStepSlider(productListElem);
                } else if (productListElem.closest('.PushCollectionHomepage')) {
                    // Push collection
                    this._createPushCollectionSlider(productListElem);
                }
            }
        }
    }

    _initSlider(productListElem, sliderOptions) {
        // Add slider classes
        productListElem.classList.add('swiper');
        productListElem.querySelector('.ProductListPage').classList.add('swiper-wrapper');
        Array.from(productListElem.querySelectorAll('.ProductCard')).forEach((el) => {
            el.classList.add('swiper-slide');
        });

        // Initialize slider
        // eslint-disable-next-line
        let swiper = new Swiper(productListElem, sliderOptions);
    }

    _createCmsSlider(productListElem) {
        const isInTutorial = !!productListElem.closest('.Tutorial-Products');
        // eslint-disable-next-line no-magic-numbers
        const breakpointSlider = isInTutorial ? 810 : 0; // 480
        const parent = productListElem.closest('[data-content-type="products"][data-appearance="carousel"]');
        const breakpointNumber = parent && parent.className && parent.className.indexOf('largeur100') !== -1 ? 1 : null;
        const defaultBreakPoint = 3;

        if (window.innerWidth < breakpointSlider) {
            this._destroySlider(productListElem);
        } else {
            // eslint-disable-next-line
            let swiper = productListElem.classList.contains('swiper')
                ? productListElem.swiper : null;

            if (swiper == null) {
                const sliderOptions = {
                    modules: [Autoplay, FreeMode, Navigation, Pagination, Scrollbar],
                    loop: false,
                    slidesPerView: 2,
                    spaceBetween: 20,
                    centerInsufficientSlides: true,
                    // autoHeight: true,
                    freeMode: {
                        enabled: true,
                        sticky: true,
                        momentumBounce: false,
                        momentumRatio: 0.5
                    },
                    breakpoints: {
                        810: {
                            slidesPerView: breakpointNumber ?? defaultBreakPoint
                        },
                        1024: {
                            slidesPerView: breakpointNumber ?? defaultBreakPoint + 1
                        }
                    }
                };

                if (parent.getAttribute('data-max-items')) {
                    const breakpointLarge = 1280;
                    sliderOptions.breakpoints[breakpointLarge] = {
                        slidesPerView: parent.getAttribute('data-max-items')
                    };
                }

                if (parent.getAttribute('data-show-arrows') === 'true') {
                    // Add slider arrows div
                    const arrowPrev = document.createElement('div');
                    arrowPrev.className = 'swiper-button-prev';
                    // productListRef.current.appendChild(arrowPrev);
                    productListElem.after(arrowPrev);
                    const arrowNext = document.createElement('div');
                    arrowNext.className = 'swiper-button-next';
                    // productListRef.current.appendChild(arrowNext);
                    productListElem.after(arrowNext);

                    sliderOptions.navigation = {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev'
                    };
                }

                if (parent.getAttribute('data-show-dots') === 'true') {
                    if (isInTutorial) {
                        // Add slider scrollbar div
                        const scrollBar = document.createElement('div');
                        scrollBar.className = 'swiper-scrollbar';
                        productListElem.appendChild(scrollBar);

                        sliderOptions.scrollbar = {
                            el: '.swiper-scrollbar',
                            draggable: true,
                            hide: false,
                            snapOnRelease: true
                        };
                    } else {
                        // Add pagination (bullets)
                        const pagination = document.createElement('div');
                        pagination.className = 'swiper-pagination';
                        productListElem.appendChild(pagination);

                        sliderOptions.pagination = {
                            clickable: true,
                            type: 'bullets',
                            el: '.swiper-pagination'
                        };
                    }
                }

                if (parent.getAttribute('data-autoplay') === 'true') {
                    sliderOptions.autoplay = {
                        delay: parseInt(parent.getAttribute('data-autoplay-speed'), 10),
                        pauseOnMouseEnter: true,
                        disableOnInteraction: false
                    };
                }

                if (parent.getAttribute('data-infinite-loop') === 'true') {
                    sliderOptions.loop = true;
                    if (sliderOptions.scrollbar) {
                        sliderOptions.scrollbar.enabled = false; // not supported with loop
                    }
                }

                // Initialize slider
                this._initSlider(productListElem, sliderOptions);
            }
        }
    }

    _createTutorialStepSlider(productListElem) {
        const breakpointSlider = 810;

        if (window.innerWidth < breakpointSlider) {
            this._destroySlider(productListElem);
        } else {
            // eslint-disable-next-line
            let swiper = productListElem.classList.contains('swiper')
                ? productListElem.swiper : null;

            if (swiper == null) {
                const sliderOptions = {
                    modules: [FreeMode, Pagination],
                    slidesPerView: 'auto',
                    spaceBetween: 20,
                    freeMode: {
                        enabled: true,
                        sticky: true,
                        momentumBounce: false,
                        momentumRatio: 0.5
                    },
                    pagination: {
                        clickable: true,
                        type: 'bullets',
                        el: '.swiper-pagination'
                    }
                };

                // Add pagination (bullets)
                const pagination = document.createElement('div');
                pagination.className = 'swiper-pagination';
                productListElem.appendChild(pagination);

                // Initialize slider
                this._initSlider(productListElem, sliderOptions);
            }
        }
    }

    _createPushCollectionSlider(productListElem) {
        const breakpointSlider = 810;

        if (window.innerWidth < breakpointSlider) {
            this._destroySlider(productListElem);
        } else {
            // eslint-disable-next-line
            let swiper = productListElem.classList.contains('swiper')
                ? productListElem.swiper : null;

            if (swiper == null) {
                const sliderOptions = {
                    modules: [FreeMode, Scrollbar],
                    slidesPerView: 'auto',
                    spaceBetween: 20,
                    freeMode: {
                        enabled: true,
                        sticky: true,
                        momentumBounce: false,
                        momentumRatio: 0.5
                    },
                    scrollbar: {
                        el: '.swiper-scrollbar',
                        draggable: true,
                        hide: false,
                        snapOnRelease: true
                    }
                };

                // Add slider scrollbar div
                const scrollBar = document.createElement('div');
                scrollBar.className = 'swiper-scrollbar';
                productListElem.appendChild(scrollBar);

                // Initialize slider
                this._initSlider(productListElem, sliderOptions);
            }
        }
    }

    _destroySlider(productListElem) {
        // eslint-disable-next-line
        let swiper = productListElem.classList.contains('swiper')
            ? productListElem.swiper : null;

        if (swiper !== null) {
            // Destroy slider
            swiper.destroy(true, true);
            swiper = null;

            // Remove slider classes (manually added)
            productListElem.classList.remove('swiper');
            productListElem.querySelector('.ProductListPage').classList.remove('swiper-wrapper');
            Array.from(productListElem.querySelectorAll('.ProductCard')).forEach((el) => {
                el.classList.remove('swiper-slide');
            });
            if (productListElem.querySelector('.swiper-scrollbar')) {
                productListElem.querySelector('.swiper-scrollbar').remove();
            }
            if (productListElem.parentNode.querySelector('.swiper-pagination')) {
                Array.from(productListElem.querySelectorAll('.swiper-pagination')).forEach((el) => {
                    el.remove();
                });
            }
            if (productListElem.parentNode.querySelector('.swiper-button-prev')) {
                Array.from(productListElem.querySelectorAll('.swiper-button-prev')).forEach((el) => {
                    el.remove();
                });
            }
            if (productListElem.parentNode.querySelector('.swiper-button-next')) {
                Array.from(productListElem.querySelectorAll('.swiper-button-next')).forEach((el) => {
                    el.remove();
                });
            }
        }
    }

    onShowMoreButtonClick() {
        this.loadPage();
    }

    /**
     * Override: remove scrollToTop on desktop
     * @param currentPage
     * @param isNext
     */
    requestPage(currentPage = 1, isNext = false) {
        const {
            sort,
            search,
            filter,
            pageSize,
            requestProductList,
            requestProductListInfo,
            noAttributes,
            noVariants,
            isWidget
        } = this.props;

        /**
         * In case the wrong category was passed down to the product list,
         * prevent it from being requested.
         */
        if (filter.categoryIds === -1) {
            return;
        }

        /**
         * Do not request page if there are no filters
         */
        if (!search && !this.isEmptyFilter()) {
            return;
        }

        const options = {
            isNext,
            noAttributes,
            noVariants,
            args: {
                sort,
                filter,
                search,
                pageSize,
                currentPage
            }
        };

        const infoOptions = {
            args: {
                filter,
                search
            }
        };

        requestProductList(options);

        if (!isWidget) {
            requestProductListInfo(infoOptions);
        }
    }

    /**
     * Override: remove scrollToTop
     * @param pageNumber
     */
    updatePage(pageNumber) {
        const { location, history } = this.props;

        setQueryParams({
            page: pageNumber === 1 ? '' : pageNumber
        }, location, history);

        // scrollToTop();
    }

    /**
     * Override
     * @returns {{productListRef: React.RefObject<unknown>, contentPushes}}
     */
    containerProps() {
        const {
            contentPushes, pageSize, totalItems, isPageLoading, categoryId
        } = this.props;

        return {
            ...super.containerProps(),
            productListRef: this.productListRef,
            contentPushes,
            pageSize,
            totalItems,
            isPageLoading,
            categoryId
        };
    }

    /**
     * Override: re-enable infinite scroll on mobile
     * @returns {boolean|*}
     * @private
     */
    _getIsInfiniteLoaderEnabled() {
        const { isInfiniteLoaderEnabled } = this.props;

        return isInfiniteLoaderEnabled;
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductListContainer));
